import axiosClient from "../axioxClient.ts/axiosClient";
import { ICategory, IModules } from "./interfaces";

/**
 * Checks whether two options (dropdown or status options) match by name (label or value).
 * Matching is case-insensitive and trims whitespace.
 */
export const isOptionNameMatch = (
  target: { label?: string; value?: string },
  candidate: { label?: string; value?: string }
): boolean => {
  const clean = (s?: string) => s?.trim().toLowerCase() || "";
  const tLabel = clean(target.label);
  const tValue = clean(target.value);
  const cLabel = clean(candidate.label);
  const cValue = clean(candidate.value);

  // If both label and value are empty in either object, cannot match
  if ((!tLabel && !tValue) || (!cLabel && !cValue)) return false;

  if (tLabel && cLabel && tLabel === cLabel) return true;
  if (tValue && cValue && tValue === cValue) return true;
  if (tLabel && cValue && tLabel === cValue) return true;
  if (tValue && cLabel && tValue === cLabel) return true;

  return false;
};

/**
 * Searches across all statusOptions modules and dropdown categories
 * to find the first matching item with a non-empty color.
 */
export const findMatchingOptionColor = (
  target: { label?: string; value?: string },
  statusOptions: IModules[] = [],
  categories: ICategory[] = [],
  exclude?: { id: number; type: "status" | "dropdown" }
): string | null => {
  const clean = (s?: string) => s?.trim().toLowerCase() || "";
  if (!clean(target.label) && !clean(target.value)) return null;

  // Search in status options
  for (const module of statusOptions) {
    for (const opt of module.statusOptions || []) {
      if (exclude?.type === "status" && opt.id === exclude.id) continue;
      if (opt.color && opt.color.trim() && isOptionNameMatch(target, opt)) {
        return opt.color;
      }
    }
  }

  // Search in dropdown options
  for (const category of categories) {
    for (const opt of category.dropdowns || []) {
      if (exclude?.type === "dropdown" && opt.id === exclude.id) continue;
      if (opt.color && opt.color.trim() && isOptionNameMatch(target, opt)) {
        return opt.color;
      }
    }
  }

  return null;
};

/**
 * Synchronizes color to all other options (status options and dropdowns)
 * whose name matches target, but currently have a different color.
 */
export const syncMatchingOptionColors = async ({
  target,
  statusOptions = [],
  categories = [],
  exclude,
}: {
  target: { label: string; value: string; color: string };
  statusOptions?: IModules[];
  categories?: ICategory[];
  exclude?: { id: number; type: "status" | "dropdown" };
}): Promise<{ updatedCount: number }> => {
  const targetColor = target.color?.trim();
  if (!targetColor) return { updatedCount: 0 };

  const updatePromises: Promise<void>[] = [];

  // Find all matching status options whose color differs
  for (const module of statusOptions) {
    for (const opt of module.statusOptions || []) {
      if (exclude?.type === "status" && opt.id === exclude.id) continue;
      if (
        isOptionNameMatch(target, opt) &&
        opt.color?.trim().toLowerCase() !== targetColor.toLowerCase()
      ) {
        updatePromises.push(
          (async () => {
            try {
              await axiosClient.put(`/options/status-option/${opt.id}`, {
                label: opt.label,
                value: opt.value,
                color: targetColor,
                order: opt.order,
                status: opt.status,
              });
            } catch (err) {
              console.error("Failed to sync status option color", err);
            }
          })()
        );
      }
    }
  }

  // Find all matching dropdown options whose color differs
  for (const category of categories) {
    for (const opt of category.dropdowns || []) {
      if (exclude?.type === "dropdown" && opt.id === exclude.id) continue;
      if (
        isOptionNameMatch(target, opt) &&
        opt.color?.trim().toLowerCase() !== targetColor.toLowerCase()
      ) {
        updatePromises.push(
          (async () => {
            try {
              await axiosClient.put(`/options/dropdowns/${opt.id}`, {
                label: opt.label,
                value: opt.value,
                color: targetColor,
                order: opt.order,
                status: opt.status,
              });
            } catch (err) {
              console.error("Failed to sync dropdown option color", err);
            }
          })()
        );
      }
    }
  }

  if (updatePromises.length > 0) {
    await Promise.all(updatePromises);
  }

  return { updatedCount: updatePromises.length };
};
